import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../utils/apiUtils';

function loadBanquestTokenization() {
  return new Promise((resolve, reject) => {
    if (window.HostedTokenization) { resolve(window.HostedTokenization); return; }
    const script = document.createElement('script');
    const base = import.meta.env.VITE_BANQUEST_SANDBOX === 'true'
      ? 'https://tokenization.sandbox.banquestgateway.com'
      : 'https://tokenization.banquestgateway.com';
    script.src = `${base}/tokenization/v0.3`;
    script.async = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.HostedTokenization) { clearInterval(check); resolve(window.HostedTokenization); }
      }, 50);
      setTimeout(() => { clearInterval(check); reject(new Error('Payment library did not initialize')); }, 8000);
    };
    script.onerror = () => reject(new Error('Failed to load payment form. Please refresh and try again.'));
    document.head.appendChild(script);
  });
}

export default function TariffPay() {
  const [searchParams] = useSearchParams();
  const regParam = searchParams.get('reg'); // Can be "123" or "123,456,789"

  const [state, setState] = useState('loading'); // loading | paymentForm | processing | success | alreadyPaid | error
  const [errorMsg, setErrorMsg] = useState('');
  const [cardError, setCardError] = useState('');
  const [personName, setPersonName] = useState('');
  const [registrationCode, setRegistrationCode] = useState('');
  const [flightLabel, setFlightLabel] = useState('');
  const [amount, setAmount] = useState(35);
  const [passengers, setPassengers] = useState([]);
  const [tokenizationReady, setTokenizationReady] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const hostedTokenizationRef = useRef(null);
  const initStartedRef = useRef(false);

  // Load registration info
  useEffect(() => {
    if (!regParam) {
      setState('error');
      setErrorMsg('Invalid link — missing registration ID.');
      return;
    }

    apiFetch(`${import.meta.env.VITE_API_URL}/tariff-info?reg=${encodeURIComponent(regParam)}`)
      .then(data => {
        if (data.status === 'success') {
          setPersonName(`${data.firstName} ${data.lastName}`);
          setRegistrationCode(data.registrationCode || '');
          setFlightLabel(data.flightLabel || '');
          setAmount(data.amount || 35);
          setPassengers(data.passengers || []);
          setState('paymentForm');
        } else if (data.status === 'already_paid') {
          setState('alreadyPaid');
        } else {
          setState('error');
          setErrorMsg(data.message || 'Something went wrong.');
        }
      })
      .catch(err => { setState('error'); setErrorMsg(err.message || 'Network error.'); });
  }, [regParam]);

  // Initialize Banquest tokenization when entering payment form
  useEffect(() => {
    if (state !== 'paymentForm' || initStartedRef.current) return;
    initStartedRef.current = true;

    const tokenizationKey = import.meta.env.VITE_BANQUEST_TOKENIZATION_KEY;
    if (!tokenizationKey) {
      setCardError('Payment system not configured.');
      return;
    }

    loadBanquestTokenization()
      .then(HostedTokenization => {
        const fieldStyle = 'border: 1px solid #d1d5db; border-radius: 8px; padding: 10px 12px; font-size: 16px; height: 44px; box-sizing: border-box; outline: none;';
        const instance = new HostedTokenization(tokenizationKey, {
          target: '#banquest-card-form',
          showZip: true,
          requireCvv2: true,
          styles: {
            container: 'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 16px; max-width: 100%; overflow: visible;',
            card: fieldStyle + ' width: 100%;',
            expiryMonth: fieldStyle + ' width: 60px; text-align: center;',
            expirySeparator: 'font-size: 20px; color: #9ca3af; padding: 0 2px; line-height: 44px;',
            expiryYear: fieldStyle + ' width: 60px; text-align: center;',
            cvv2: fieldStyle + ' width: 70px; text-align: center;',
            avsZip: fieldStyle + ' width: 100%;',
            labelType: 'static-top',
            labels: 'font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 6px; display: block;',
          },
        });
        instance.on('ready', () => setTokenizationReady(true));
        instance.on('input', ({ error }) => {
          if (error) setCardError(error.message || 'Please check your card details');
          else setCardError('');
        });
        hostedTokenizationRef.current = instance;
      })
      .catch(err => setCardError(err.message));

    return () => {
      if (hostedTokenizationRef.current) {
        try { hostedTokenizationRef.current.destroy(); } catch (_) {}
        hostedTokenizationRef.current = null;
      }
    };
  }, [state]);

  const handlePay = useCallback(async () => {
    if (!tokenizationReady || !hostedTokenizationRef.current || state === 'processing') return;
    setCardError('');

    let result;
    try {
      result = await hostedTokenizationRef.current.getNonceToken();
    } catch (err) {
      setCardError(err?.message || 'Please check your card details and try again.');
      return;
    }
    if (!result?.nonce) {
      setCardError('Could not process card. Please try again.');
      return;
    }

    setState('processing');
    try {
      const response = await apiFetch(`${import.meta.env.VITE_API_URL}/tariff-charge`, {
        method: 'POST',
        body: JSON.stringify({
          reg: regParam,
          nonce: result.nonce,
          expiryMonth: result.expiryMonth,
          expiryYear: result.expiryYear,
          avsZip: result.avsZip || undefined,
        }),
      });

      if (response.status === 'success') {
        setPaymentResult(response);
        setAmount(response.amount || amount);
        setState('success');
      } else if (response.status === 'already_paid') {
        setState('alreadyPaid');
      } else if (response.status === 'declined') {
        setCardError(response.message || 'Payment declined. Please try a different card.');
        setState('paymentForm');
      } else {
        setCardError(response.message || 'Payment failed. Please try again.');
        setState('paymentForm');
      }
    } catch (err) {
      setCardError(err.message || 'Network error. Please try again.');
      setState('paymentForm');
    }
  }, [tokenizationReady, state, regParam, amount]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-700 px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-4 mb-3">
            <img src="/igud-logo.png" alt="Igud Yeshivos" className="h-10 w-auto" />
            <img src="/ehk-logo_2-1.png" alt="Eretz Hakodesh" className="h-10 w-auto" />
          </div>
          <h1 className="text-white font-bold text-xl">Border Crossing Tariff</h1>
        </div>

        <div className="px-6 py-8">
          {/* Loading */}
          {state === 'loading' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading...</p>
            </div>
          )}

          {/* Error */}
          {state === 'error' && (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">&#9888;&#65039;</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
              <p className="text-gray-600">{errorMsg}</p>
            </div>
          )}

          {/* Already Paid */}
          {state === 'alreadyPaid' && (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">&#9989;</div>
              <h2 className="text-xl font-bold text-green-700 mb-2">Already Paid</h2>
              <p className="text-gray-600">The border tariff surcharge has already been paid. No further action is needed.</p>
            </div>
          )}

          {/* Payment Form */}
          {state === 'paymentForm' && (
            <div>
              <div className="text-center mb-4">
                {passengers.length > 1 ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-500 mb-2">Paying tariff for {passengers.length} passengers:</p>
                    <ul className="text-sm text-blue-800 font-semibold">
                      {passengers.map(p => (
                        <li key={p.id}>{p.name}</li>
                      ))}
                    </ul>
                  </div>
                ) : personName && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-500">Paying tariff for</p>
                    <p className="text-lg font-bold text-blue-800">{personName}</p>
                    {registrationCode && <p className="text-xs text-gray-400 mt-1">Code: {registrationCode}</p>}
                  </div>
                )}
                {flightLabel && <p className="text-blue-700 text-xs">{flightLabel}</p>}
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5 text-sm text-red-800">
                <p className="font-semibold mb-1">Outstanding Border Tariff</p>
                <p>The border crossing tariff surcharge was not collected at the border and is still outstanding. Please complete your payment below.</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-5 mb-5 text-center">
                <p className="text-sm text-gray-500 mb-1">Amount Due</p>
                <p className="text-4xl font-bold text-gray-800">${amount}.00</p>
                {passengers.length > 1 && (
                  <p className="text-xs text-gray-400 mt-1">${passengers.length} passengers x $35 each</p>
                )}
              </div>

              <div id="banquest-card-form" className="mb-4 overflow-visible" style={{ height: 200 }}>
                {!tokenizationReady && (
                  <div className="flex items-center justify-center" style={{ height: 200 }}>
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>

              {cardError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
                  {cardError}
                </div>
              )}

              <button
                onClick={handlePay}
                disabled={!tokenizationReady || state === 'processing'}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
                  tokenizationReady ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Pay ${amount}.00
              </button>

              <p className="text-xs text-gray-400 text-center mt-3">
                Your card statement will show a charge from <strong>CCJH</strong>.
              </p>
            </div>
          )}

          {/* Processing */}
          {state === 'processing' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-semibold">Processing payment...</p>
              <p className="text-gray-400 text-sm mt-1">Please do not close this page.</p>
            </div>
          )}

          {/* Success */}
          {state === 'success' && (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">&#9989;</div>
              <h2 className="text-xl font-bold text-green-700 mb-2">Payment Successful!</h2>
              <p className="text-gray-600 mb-4">Your border tariff surcharge of <strong>${amount}.00</strong> has been paid{paymentResult?.passengerCount > 1 ? ` for ${paymentResult.passengerCount} passengers` : ''}.</p>
              {paymentResult && (
                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                  {paymentResult.cardType && <p>Card: {paymentResult.cardType} ending in {paymentResult.last4}</p>}
                  {paymentResult.referenceNumber && <p>Reference: {paymentResult.referenceNumber}</p>}
                </div>
              )}
              <p className="text-sm text-gray-400 mt-4">You can close this page.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-100 px-6 py-4 text-center text-xs text-gray-500">
          Questions? Email <a href="mailto:flights@igudys.com" className="text-blue-600">flights@igudys.com</a>
        </div>
      </div>
    </div>
  );
}
